import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  CheckCircle,
  FileEdit,
  Eye,
  Edit,
} from 'lucide-react';
import { getSupabaseAdminClient, SSWContent } from '@parentpath/supabase-client';
import { useAdminLocale } from '../../hooks/useAdminLocale';
import { LocaleSelector } from '../../components/LocaleSelector';

const supabase = getSupabaseAdminClient();

type FilterType = 'all' | 'published' | 'draft';

export function SSWContentPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterType>('all');
  const { contentLocale } = useAdminLocale();

  // Fetch SSW content for selected locale
  const { data: sswContent, isLoading } = useQuery({
    queryKey: ['ssw-content', contentLocale],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ssw_content')
        .select('*')
        .eq('locale', contentLocale)
        .order('week_number', { ascending: true });

      if (error) throw error;
      return data as SSWContent[];
    },
  });

  // Create a map of existing weeks
  const weekMap = new Map<number, SSWContent>();
  sswContent?.forEach((week) => {
    weekMap.set(week.week_number, week);
  });

  // Generate array for weeks 1-42
  const allWeeks = Array.from({ length: 42 }, (_, i) => i + 1);

  // Filter weeks based on selection
  const filteredWeeks = allWeeks.filter((weekNum) => {
    const content = weekMap.get(weekNum);
    if (filter === 'all') return true;
    if (filter === 'published') return content?.is_published === true;
    if (filter === 'draft') return !content || content.is_published === false;
    return true;
  });

  const stats = {
    total: 42,
    published: sswContent?.filter((c) => c.is_published).length || 0,
    draft: 42 - (sswContent?.filter((c) => c.is_published).length || 0),
  };

  const getWeekStatus = (weekNum: number) => {
    const content = weekMap.get(weekNum);
    if (!content) return 'missing';
    if (content.is_published) return 'published';
    return 'draft';
  };

  const getWeekStatusBadge = (status: string) => {
    switch (status) {
      case 'published':
        return (
          <div className="flex items-center gap-1 text-emerald-600">
            <CheckCircle className="w-3 h-3" />
            <span className="text-xs">Veröffentlicht</span>
          </div>
        );
      case 'draft':
        return (
          <div className="flex items-center gap-1 text-amber-600">
            <FileEdit className="w-3 h-3" />
            <span className="text-xs">Entwurf</span>
          </div>
        );
      default:
        return (
          <div className="flex items-center gap-1 text-slate-400">
            <Plus className="w-3 h-3" />
            <span className="text-xs">Fehlt</span>
          </div>
        );
    }
  };

  const handleWeekClick = (weekNum: number) => {
    navigate(`/content/ssw/${weekNum}/edit`);
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">SSW-Inhalte (1-42)</h1>
          <p className="text-slate-600">Schwangerschaftswochen-Content verwalten</p>
        </div>
        <LocaleSelector />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="text-2xl font-bold text-slate-900">{stats.total}</div>
          <div className="text-sm text-slate-600">Gesamt SSW</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="text-2xl font-bold text-emerald-600">{stats.published}</div>
          <div className="text-sm text-slate-600">Veröffentlicht</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="text-2xl font-bold text-amber-600">{stats.draft}</div>
          <div className="text-sm text-slate-600">Entwürfe / Fehlend</div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 flex gap-4">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            filter === 'all'
              ? 'bg-teal-500 text-white'
              : 'bg-slate-200 hover:bg-slate-300 text-slate-700'
          }`}
        >
          Alle ({stats.total})
        </button>
        <button
          onClick={() => setFilter('published')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            filter === 'published'
              ? 'bg-teal-500 text-white'
              : 'bg-slate-200 hover:bg-slate-300 text-slate-700'
          }`}
        >
          Veröffentlicht ({stats.published})
        </button>
        <button
          onClick={() => setFilter('draft')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            filter === 'draft'
              ? 'bg-teal-500 text-white'
              : 'bg-slate-200 hover:bg-slate-300 text-slate-700'
          }`}
        >
          Entwürfe ({stats.draft})
        </button>
      </div>

      {/* SSW Grid */}
      {isLoading ? (
        <div className="text-center py-12 text-slate-500">Lädt...</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4">
          {filteredWeeks.map((weekNum) => {
            const content = weekMap.get(weekNum);
            const status = getWeekStatus(weekNum);

            return (
              <div
                key={weekNum}
                onClick={() => handleWeekClick(weekNum)}
                className={`
                  bg-white rounded-lg shadow-sm p-4 cursor-pointer transition-all hover:shadow-md hover:scale-105
                  ${status === 'published' ? 'border-2 border-emerald-200' : ''}
                  ${status === 'draft' ? 'border-2 border-amber-200' : ''}
                  ${status === 'missing' ? 'border-2 border-dashed border-slate-200' : ''}
                `}
              >
                <div className="text-center">
                  <div className="text-3xl font-bold text-slate-900 mb-1">
                    {weekNum}
                  </div>
                  <div className="text-xs text-slate-500 mb-2">SSW</div>
                  {getWeekStatusBadge(status)}
                </div>
                <div className="mt-3 pt-3 border-t border-slate-100 flex justify-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleWeekClick(weekNum);
                    }}
                    className="p-1 hover:bg-slate-100 rounded"
                    title="Bearbeiten"
                  >
                    <Edit className="w-4 h-4 text-slate-500" />
                  </button>
                  {content && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        // Preview functionality
                      }}
                      className="p-1 hover:bg-slate-100 rounded"
                      title="Vorschau"
                    >
                      <Eye className="w-4 h-4 text-slate-500" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
